import { Injectable } from '@angular/core';
import { AppRoute } from 'src/app/constants/app.route';
import { environment } from 'src/environments/environment';
import { generateCodeChallenge, toParams } from '../../utils/utils';
import { AuthProvider } from '../http/general/enums/auth-provider';
import { InHouseProvider } from './interfaces/in-house-provider';
import { ProviderItem } from './interfaces/provider-item';
import { StateParameter } from './interfaces/state-parameter';

@Injectable({
  providedIn: 'root'
})
export class ProviderBuilderService {
  providerLinkBuilders = {
    [AuthProvider.Google]: this.getGoogle,
    [AuthProvider.ClassLink]: this.getClassLink,
    [AuthProvider.Clever]: this.getClever,
    [AuthProvider.GG4L]: this.getGG4L,
    [AuthProvider.Microsoft]: this.getMicrosoft,
    [AuthProvider.Edlink]: this.getEdlink,
  } as Record<AuthProvider, (verifierCode: string, challengeCode: string) => { url: string; verifierCode: string }>;

  async getProviders(): Promise<ProviderItem[]> {
    return [
      {
        authProvider: AuthProvider.Google,
        logoTitle: 'Google logo',
        title: 'Google',
        logo: 'assets/images/google-icon-color.svg',
        dedicatedRouterPath: AppRoute.GoogleLogin
      },
      {
        authProvider: AuthProvider.Clever,
        logoTitle: 'Clever logo',
        title: 'Clever',
        logo: 'assets/images/clever-icon.svg',
        dedicatedRouterPath: AppRoute.CleverLogin
      },
      {
        authProvider: AuthProvider.Microsoft,
        logoTitle: 'Microsoft logo',
        title: 'Microsoft',
        logo: 'assets/images/office-icon.svg',
        dedicatedRouterPath: AppRoute.MicrosoftLogin
      },
      // {
      //   authProvider: AuthProvider.Edlink,
      //   logoTitle: 'Edlink logo',
      //   title: 'SIS/LMS',
      //   logo: 'assets/images/edlink-icon.svg',
      //   dedicatedRouterPath: AppRoute.EdlinkLogin
      // },
      {
        authProvider: AuthProvider.ClassLink,
        logoTitle: 'ClassLink logo',
        title: 'ClassLink',
        logo: 'assets/images/classlink-icon.svg',
        dedicatedRouterPath: AppRoute.ClasslinkLogin
      },
      {
        authProvider: AuthProvider.GG4L,
        logoTitle: 'GG4L logo',
        title: 'GG4L',
        logo: 'assets/images/gg4l-icon.svg'
      }
    ];
  }

  async getProviderLinkByProvider(
    authProvider: AuthProvider
  ): Promise<{ url: string; verifierCode: string } | undefined> {
    if (this.providerLinkBuilders[authProvider]) {
      const { verifier, challenge } = await generateCodeChallenge();

      return this.providerLinkBuilders[authProvider].bind(this)(verifier, challenge);
    }

    return;
  }

  async getInHouseProvider(): Promise<InHouseProvider> {
    const { verifier, challenge } = await generateCodeChallenge();
    const state: StateParameter = { providerId: AuthProvider.Eduspire };

    return {
      client_id: environment.ProvidersClientIds.Eduspire,
      redirect_uri: environment.AuthCallBackUrl,
      response_type: 'code',
      scope: 'email profile openid offline_access',
      code_challenge: challenge,
      code_challenge_method: 'S256',
      state: this.getState(state),
      verifierCode: verifier
    };
  }

  private getGoogle(verifierCode: string, challengeCode: string): { url: string; verifierCode: string } {
    const state: StateParameter = { providerId: AuthProvider.Google };

    return {
      url: [
        `https://accounts.google.com/o/oauth2/v2/auth/oauthchooseaccount`,
        `?client_id=${environment.ProvidersClientIds.Google}`,
        `&redirect_uri=${encodeURIComponent(environment.AuthCallBackUrl)}`,
        `&response_type=code`,
        `&state=${this.getState(state)}`,
        `&prompt=consent`,
        `&access_type=offline`,
        `&scope=email profile openid`,
        `&code_challenge_method=S256`,
        `&code_challenge=${challengeCode}`,
        `&flowName=GeneralOAuthFlow`
      ].join(''),
      verifierCode
    };
  }

  private getClever(verifierCode: string, challengeCode: string): { url: string; verifierCode: string } {
    const state: StateParameter = { providerId: AuthProvider.Clever };

    return {
      url: [
        `https://clever.com/oauth/authorize`,
        `?client_id=${environment.ProvidersClientIds.Clever}`,
        `&redirect_uri=${encodeURIComponent(environment.AuthCallBackUrl)}`,
        `&state=${this.getState(state)}`,
        `&response_type=code`,
        `&code_challenge_method=S256`,
        `&code_challenge=${challengeCode}`
      ].join(''),
      verifierCode
    };
  }

  private getMicrosoft(verifierCode: string, challengeCode: string): { url: string; verifierCode: string } {
    const state: StateParameter = { providerId: AuthProvider.Microsoft };

    return {
      url: [
        `https://login.microsoftonline.com/common/oauth2/v2.0/authorize`,
        `?client_id=${environment.ProvidersClientIds.Microsoft}`,
        `&response_type=code`,
        `&state=${this.getState(state)}`,
        `&redirect_uri=${encodeURIComponent(environment.AuthCallBackUrl)}`,
        `&response_mode=query`,
        `&scope=https://graph.microsoft.com/user.read openid profile email offline_access`,
        `&code_challenge_method=S256`,
        `&code_challenge=${challengeCode}`
      ].join(''),
      verifierCode
    };
  }

  private getClassLink(verifierCode: string, challengeCode: string): { url: string; verifierCode: string } {
    const state: StateParameter = { providerId: AuthProvider.ClassLink };

    return {
      url: [
        `https://launchpad.classlink.com/oauth2/v2/auth`,
        `?scope=profile`,
        `&redirect_uri=${encodeURIComponent(environment.AuthCallBackUrl)}`,
        `&client_id=${environment.ProvidersClientIds.ClassLink}`,
        `&response_type=code`,
        `&state=${this.getState(state)}`,
        `&code_challenge_method=S256`,
        `&code_challenge=${challengeCode}`
      ].join(''),
      verifierCode
    };
  }

  private getGG4L(verifierCode: string, challengeCode: string): { url: string; verifierCode: string } {
    const state: StateParameter = { providerId: AuthProvider.GG4L };

    return {
      url: [
        `https://sso.gg4l.com/oauth/auth`,
        `?redirect_uri=${encodeURIComponent(environment.AuthCallBackUrl)}`,
        `&client_id=${environment.ProvidersClientIds.GG4L}`,
        `&response_type=code`,
        `&state=${this.getState(state)}`,
        `&code_challenge_method=S256`,
        `&code_challenge=${challengeCode}`
      ].join(''),
      verifierCode
    };
  }

  private getEdlink(verifierCode: string, challengeCode: string): { url: string; verifierCode: string } {
    const state: StateParameter = { providerId: AuthProvider.Edlink };

    return {
      url: [
        `https://ed.link/sso/login`,
        `?redirect_uri=${encodeURIComponent(environment.AuthCallBackUrl)}`,
        `&client_id=${environment.ProvidersClientIds.Edlink}`,
        `&response_type=code`,
        `&state=${this.getState(state)}`,
        `&code_challenge_method=S256`,
        `&code_challenge=${challengeCode}`
      ].join(''),
      verifierCode
    };
  }

  private getState(state: StateParameter): string {
    return encodeURIComponent(toParams(state).toString());
  }
}
